import React, {useRef, useState, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {
    getAppDefaultsData,
    getDataFresh,
    getDepartmentsData,
    getPartOrderData,
    getTrackingOptionsData
} from "../../state/selectors";
import LoadingSpinner from "../Utils/Spinner";
import { Box, Button, Column, Columns, Input, Select, Table, TextArea, Title } from "@allxsmith/bestax-bulma";
import ContactSection from "../Contact/ContactSection";
import {createSetDataFreshAction, createSetPartOrderData, createSetPartOrderFieldData} from "../../actions/dataActions";
import axios from "axios";

// B7: turn a save/complete error into a message that names the problem field(s).
function fmtErr(err, fallback) {
    const d = err && err.response && err.response.data;
    if (!d) return fallback;
    if (typeof d === "string") return d;
    if (d.detail) return d.detail;
    // Neil 9 Oct: flatten DRF-style nested errors (incl. the parts list) into a readable message that
    // NAMES the field - e.g. a missing supplier on a part line - instead of showing "[object Object]".
    const LABELS = {supplier: "supplier", name: "part name", part_number: "part number",
        customer_tracking_option: "customer tracking option", supplier_tracking_option: "supplier tracking option",
        tracking_option: "tracking option", customer_cost: "customer charge", cost: "cost"};
    const msgs = [];
    const walk = (val, path) => {
        if (val == null || val === "") return;
        if (typeof val === "string") { msgs.push((path ? path + ": " : "") + val); return; }
        if (Array.isArray(val)) {
            val.forEach((v, i) => {
                const p = (v && typeof v === "object" && !Array.isArray(v)) ? (path ? path + " " + (i + 1) : "line " + (i + 1)) : path;
                walk(v, p);
            });
            return;
        }
        if (typeof val === "object") {
            Object.keys(val).forEach(k => walk(val[k], (path ? path + " " : "") + (LABELS[k] || k)));
        }
    };
    try {
        if (d.parts) walk(d.parts, "Part");
        Object.keys(d).forEach(k => { if (k !== "parts") walk(d[k], (LABELS[k] || k)); });
        const uniq = [...new Set(msgs)].slice(0, 6);
        return uniq.length ? ("Please check - " + uniq.join("; ")) : fallback;
    } catch (e) { return fallback; }
}
import {ToastContainer, toast} from "react-toastify";
import useButtonSelect from "../../hooks/useButtonSelect";
import DepartmentPicker from "../Job/DepartmentPicker";
import useSafeSave from "../../hooks/useSafeSave";
import {fixEmptyDepartment, getPartCustomerCost} from "../../utils";
import PartRow from "./PartRow";
import MaxusPartSearch from "./MaxusPartSearch";
import MaxusCatalogueOverlay from "./MaxusCatalogueOverlay";
import PartSearchModal from "../Job/PartSearchModal";
import WorkSearchModal from "../Job/WorkSearchModal";
import {useReactToPrint} from "react-to-print";
import PrintPartOrder from "./PrintPartOrderSummary";
import PrintQuote from "./PrintQuote";
import {TrackingOptionModal} from "./TrackingOptionsModal";
import FedexLabelModal from "./FedexLabelModal";
import LabelPrinterModal from "./LabelPrinterModal";
import {printZpl} from "./labelPrinter";
import CashSaleSection from "../Contact/CashSaleSection";
import TrackingOptionsPicker from "../Settings/TrackingCategory/TrackingOptionsPicker";
import Nav from "../Nav/Nav";

// The email the order's emails go to: an on-system customer's own email, else the delivery email
// (mirrors workshop/services/emails._recipient; Neil 12 Oct).
const orderEmail = (po) => {
    // 13 Oct: cash-sale quotes carry the buyer's email on the cash-sale row (same order as the
    // server's _recipient) - without this the Message customer box stayed hidden on quotes.
    const cs = (po && Array.isArray(po.cash_sale)) ? po.cash_sale.find(x => x && x.email) : null;
    if (cs) return cs.email;
    const c = (po && po.customer) || null;
    if (c && typeof c === "object" && !c.is_cash_sale) {
        const e = c.email || c.parts_email || c.company_email;
        if (e) return e;
    }
    return (po && po.delivery_contact_email) || "";
};



// FedEx tracking numbers (12-15 digits) open FedEx's tracking page (13 Oct, Neil)
const fedexTrackUrl = (t) => "https://www.fedex.com/fedextrack/?trknbr=" + encodeURIComponent(t);
const linkTracking = (text) => {
    if (!text || !/\b\d{12,15}\b/.test(text)) return text;
    return text.split(/(\b\d{12,15}\b)/).map((bit, i) => /^\d{12,15}$/.test(bit)
        ? <a key={i} href={fedexTrackUrl(bit)} target="_blank" rel="noopener noreferrer">{bit}</a> : bit);
};

export default function PartOrderForm(props){
    const dispatch = useDispatch();
    const printPartRef = useRef();
    const printOrderQuoteRef = useRef();

    const handlePrint =  useReactToPrint({contentRef: printPartRef});
    const handleQuotePrint = useReactToPrint({contentRef: printOrderQuoteRef});

    const [completeModalOpen, setCompleteModalOpen] = useState(false);
    const [auditPopup, setAuditPopup] = useState(null); // {title, text} - full message from the audit trail
    const [lastChange, setLastChange] = useState();
    const [vehLookup, setVehLookup] = useState(null);
    const [vehLookupLoading, setVehLookupLoading] = useState(false);
    const [stageBusy, setStageBusy] = useState(false);
    const [fedexOpen, setFedexOpen] = useState(false);
    const [fedexReplace, setFedexReplace] = useState(false);
    const [printerOpen, setPrinterOpen] = useState(false);
    const [trackBusy, setTrackBusy] = useState(false);
    const [fedexBusy, setFedexBusy] = useState(false);
    const [canRefund, setCanRefund] = useState(false);
    const [events, setEvents] = useState([]);
    const [refundMode, setRefundMode] = useState("rule");
    const [refundValue, setRefundValue] = useState("");
    const [refundBusy, setRefundBusy] = useState(false);
    const [refundResult, setRefundResult] = useState(null);
    const [refundSuggestion, setRefundSuggestion] = useState(null);
    const [refundOpen, setRefundOpen] = useState(false);
    const [payLink, setPayLink] = useState(null);
    const [payBusy, setPayBusy] = useState(false);
    const [messageText, setMessageText] = useState("");
    const [qcMargin, setQcMargin] = useState("");
    const [qcHours, setQcHours] = useState("");
    const [qcRate, setQcRate] = useState("");
    const [qcResult, setQcResult] = useState(null);
    const [vehPartsHist, setVehPartsHist] = useState(null);
    const [messageBusy, setMessageBusy] = useState(false);
    const [partsModalState, setPartsModalState] = useState({
        modalOpen: false,
        currentSearch: "",
        searchText: "",
        partIndex: 0,
        workIndex: 0
    });

    const [workModalState, setWorkModalState] = useState({
        modalOpen: false,
        currentSearch: "",
        searchText: "",
        workIndex: 0
    });



    const dataFresh = useSelector(getDataFresh);
    let partOrder = useSelector(getPartOrderData);
    let appDefaults = useSelector(getAppDefaultsData);
    let departments = useSelector(getDepartmentsData);
    let trackingOptions = useSelector(getTrackingOptionsData);





    const options = [
        {name: "Order", value: "ORDER"},
        {name: "Quote", value: "QUOTE"},
        {name: "Enquiry", value: "ENQUIRY"}
    ];

    const customerPaymentOptions = [
        {name:"", value: "BLANK"},
        {name: "BACS", value: "BACS"},
        {name: "CASH", value: "CASH"},
        {name: "CARD", value: "CARD"},
        {name: "WORLD PAY", value: "WORLD PAY"},
        {name: "DIRECT DEBIT", value: "DIRECT DEBIT"}
    ];


    const checkNotUpdated = () => {
        if (partOrder.id) {
          return axios
            .get(props.endpoint + "partorder/" + partOrder.id)
            .then(
              result => {
                return (new Date(result.data.last_saved).getTime() -
                  new Date(partOrder.last_saved).getTime()) /
                1000 <
                1}
            ).catch(err => {
              toast.error(fmtErr(err, "An error occurred!"), {position: "top-right"});
            });
        }
        return new Promise((res, rej) => res(true));
    };
    const validator = data => {
        let valid = true;
        if(!(!!data.customer_vehicle)){
            valid = false;
            toast.warning("Customer Vehicle information is required", {position: "top-right"});
        }
        if(!partOrder.customer || !partOrder.customer.id){
            valid = false;
            toast.warning("Please select a customer from the dropdown", {position: "top-right"});
        }
        else if(partOrder.customer.is_cash_sale){
            if(partOrder.cash_sale.length === 0) {
                valid = false;
                toast.warning("Cash sale details are required", {position: "top-right"});
            }
            else{
                if(!(!!partOrder.cash_sale[0].name)){
                    valid = false;
                    toast.warning("Cash Sale Name is required", {position: "top-right"});
                }
                if(!(!!partOrder.cash_sale[0].address)){
                    valid = false;
                    toast.warning("Cash Sale Address is required", {position: "top-right"});
                }
            }

        }
        data.parts.forEach(part => {
            if(!(!!part.customer_tracking_option) || !(!!part.supplier_tracking_option)){
                valid = false;
                toast.warning("Tracking Options on parts are required", {position: "top-right"});
            }
        })
        return valid;
    };


    const onComplete = () => {
        const conf = {
            url: props.endpoint+"partorderapprove",
            method: "post",
            data: partOrder
        }
        axios(conf).then(res => {
            dispatch(createSetDataFreshAction(dataFresh + 1))
        }).catch(err => {
            toast.error((err && err.response && err.response.data && err.response.data.detail) || "There was an error completing that part order", {position: "top-right"});
        })
    }

    const onCompleteClick = () => {
        setCompleteModalOpen(true);
    }




    const save = () => {
        let partOrderToSave = fixEmptyDepartment(partOrder)
        const url = partOrder.id ? props.endpoint+`partorder/${partOrder.id}` : props.endpoint+'createpartorder';
        const method = partOrder.id ? "put" : "post";

        const conf = {
            url,
            method,
            data: partOrderToSave
        }
        if(validator(partOrderToSave)) {
            axios(conf).then(res => {
                dispatch(createSetPartOrderData(res.data));
                if (!partOrder.id) {
                    window.open("/editpartorder/" + res.data.id, "_self");
                }
            }).catch(err => {
                toast.error(fmtErr(err, "There was an error saving"), {position: "top-right"});
            })
        } else {
            toast.warning("Missing Details", {position: "top-right"});
        }
    }
    const [makeDirty, safeSave] = useSafeSave(
        save,
        lastChange,
        null,
        checkNotUpdated
    )

    const handleSave = () => {
        //safeSave()
        save();
    };

    const triggerChange = (data) => {
        makeDirty();
        setLastChange(new Date());
        dispatch(createSetPartOrderFieldData(data))
    };

    // Neil 8 Oct: a brand-new part order defaults to the Van Parts Direct department, so the office
    // isn't re-picking it every time and it lands on the Van Parts tiles. Set directly (not via
    // triggerChange) so a blank new order isn't marked dirty; still changeable in the dropdown.
    useEffect(() => {
        if (!partOrder.id && !(partOrder.department && partOrder.department.id) && departments && departments.results) {
            const vp = departments.results.find(d => (d.name || "").toLowerCase().includes("van parts"));
            // Neil/Jason 9 Oct: a new part order defaults to the Van Parts department AND to a QUOTE
            // (not an order) - set together, once, on first load of a new order.
            const patch = {status: "QUOTE"};
            if (vp) patch.department = vp;
            dispatch(createSetPartOrderFieldData(patch));
        }
    }, [departments, partOrder.id]);

    // Neil 9 Oct: the order-level Tracking Option is required to complete. Default it to "Van Parts
    // Direct" for Van Parts orders so Awaiting Parts / complete doesn't throw a tracking-option error.
    useEffect(() => {
        const isVP = partOrder.department && partOrder.department.id === 9;
        if (isVP && !(partOrder.tracking_option && partOrder.tracking_option.id) && trackingOptions && trackingOptions.results) {
            const vp = trackingOptions.results.find(t => (t.name || "").toLowerCase() === "van parts direct");
            if (vp) dispatch(createSetPartOrderFieldData({tracking_option: vp}));
        }
    }, [trackingOptions, partOrder.department, partOrder.tracking_option]);

    // Resolve the MAXUS supplier contact once, so Maxus-website parts can be stamped with it on add.
    useEffect(() => {
        axios.get("/api/contacts?contact_type=SP&search=MAXUS")
            .then(resp => {
                const list = (resp.data && (resp.data.results || resp.data)) || [];
                const m = list.find(c => (c.display_name || "").toUpperCase() === "MAXUS") || list[0] || null;
                if (m) setMaxusSupplier(m);
            }).catch(() => {});
    }, []);

    // Van Parts account/cash status + credit, keyed on the CUSTOMER so it works on a new/unsaved
    // quote as soon as the customer is chosen (limit from the account page; owed from our records,
    // or Xero when connected; remaining = limit - owed). Read-only.
    useEffect(() => {
        const cid = partOrder.customer && partOrder.customer.id;
        if (cid) {
            axios.get("/api/customer-credit/by-contact/" + cid)
                .then(r => setCreditInfo(r.data)).catch(() => setCreditInfo(null));
        } else {
            setCreditInfo(null);
        }
    }, [partOrder.customer]);

    const onChange = e => {
        const newData = {[e.target.name]: e.target.value}
        triggerChange(newData);
    };

    const onDelete = () => {
        const conf = {
            url: props.endpoint+`partorder/${partOrder.id}`,
            method: "delete"
        };
        axios(conf).then(res => {
            window.open("/partorders", "_self")
        }).catch(err => {
            toast.error("There was an error deleting that part order", {position: "top-right"});
        })
    }



    const onDepartmentChange = e => {
        const toUpdate = {
            target: {
                name: "department",
                value: departmentObjects[e.target.value]
            }
        };
        onChange(toUpdate);
    };

    const onTrackingOptionChange = e => {
        const toUpdate = {
            target: {
                name: "tracking_option",
                value: trackingOptionObjects[e.target.value]
            }
        }
        onChange(toUpdate);
    }

    const onBulkPartTrackingOptionChange = (e) => {
        let parts = [...partOrder.parts];
        parts.forEach((part) => {
            part[e.target.name] = trackingOptionObjects[e.target.value]
        });
        triggerChange({parts: parts});
    }

    const onPartChange = (part, index) => {
        let parts = [...partOrder.parts];
        parts[index] = part;
        triggerChange({parts: parts});
    };

    const onPartDelete = (part, index) => {
        if(part.id) {
            const conf = {
                url: props.endpoint + `parts/update/${part.id}`,
                method: "delete"
            }
            axios(conf).then(res => {
                dispatch(createSetDataFreshAction(dataFresh + 1));
            })
        }
        else {
            partOrder.parts.splice(index, 1);
            triggerChange({parts: partOrder.parts})
        }
    }


    const searchSelectPart = (part, partIndex, workIndex) => {
        // part and work index do not do anything but are passed to function because it is used somewhere else

        let newPart = {
          name: part.name,
          part_number: part.part_number,
          supplier: part.supplier,
          cost: part.cost,
          estimated_cost: 0,
          customer_cost: getPartCustomerCost(part, partOrder.customer, appDefaults),
          created_from_lookup: part.id,
          to_be_ordered: true
        };

        let currentParts = partOrder.parts ? partOrder.parts : [];
        currentParts.push(newPart);
        triggerChange({parts: currentParts});
        setPartsModalState({...partsModalState, modalOpen: false});
    };

    const [catalogueOpen, setCatalogueOpen] = useState(false);
    const [postageOpen, setPostageOpen] = useState(false);
    const [postageBusy, setPostageBusy] = useState(false);
    const [postageRes, setPostageRes] = useState(null);
    const [postagePc, setPostagePc] = useState("");
    const [fedexSupplier, setFedexSupplier] = useState(null);
    const [dvrSupplier, setDvrSupplier] = useState(null);
    const [postageReqOpen, setPostageReqOpen] = useState(false);
    const [postageReqAmount, setPostageReqAmount] = useState("");
    const [postageReqCarrier, setPostageReqCarrier] = useState("FedEx");
    const [postageReqBusy, setPostageReqBusy] = useState(false);
    const [maxusSupplier, setMaxusSupplier] = useState(null);
    const [creditInfo, setCreditInfo] = useState(null);

    const onMaxusPartSelect = (r) => {
        // A1 + Neil 9 Oct: add a Maxus website product as a part line, PRE-FILLED to save the office
        // time - supplier = MAXUS and both tracking options = Van Parts Direct, charged like a website
        // order (customer pays the website price; cost 0 for the office to enter the Maxus cost).
        const vpTrack = (trackingOptions.results || []).find(t => (t.name || "").toLowerCase() === "van parts direct") || null;
        let newPart = {
            name: r.name,
            part_number: r.part_number,
            supplier: maxusSupplier,
            supplier_tracking_option: vpTrack,
            customer_tracking_option: vpTrack,
            cost: 0,
            customer_cost: Number(r.price) || 0,
            cost_override: true,
            from_stock: false,
            to_be_ordered: true,
            cost_vat: "INPUT2",
            created_from_maxus: r.part_number,
        };
        let currentParts = partOrder.parts ? partOrder.parts : [];
        currentParts.push(newPart);
        triggerChange({parts: currentParts});
    };

    // 14 Oct (Jason): carriage priced exactly like the website checkout - FedEx Priority on the
    // parts' website weights + 0.70 handling; large "freight-quote" items need a delivery quote.
    const guessPostcode = () => {
        const re = /\b([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})\b/i;
        const cs = (partOrder.cash_sale && partOrder.cash_sale[0]) || {};
        for (const t of [partOrder.delivery_postcode, partOrder.delivery_address, cs.address,
                         partOrder.customer && partOrder.customer.postcode]) {
            const m = re.exec((t || "").toUpperCase());
            if (m) return m[1] + " " + m[2];
        }
        return "";
    };

    const openPostage = () => {
        setPostagePc(postagePc || guessPostcode());
        setPostageRes(null);
        setPostageOpen(true);
        if (!fedexSupplier) {
            axios.get("/api/contacts?contact_type=SP&search=FEDEX")
                .then(resp => {
                    const list = (resp.data && (resp.data.results || resp.data)) || [];
                    const f = list.find(c => (c.display_name || "").toUpperCase().startsWith("FEDEX")) || null;
                    if (f) setFedexSupplier(f);
                }).catch(() => {});
        }
        if (!dvrSupplier) {   // 14 Oct: large items can go on the DVR van
            axios.get("/api/contacts?contact_type=SP&search=DIRECT VEHICLE")
                .then(resp => {
                    const list = (resp.data && (resp.data.results || resp.data)) || [];
                    const d = list.find(c => (c.display_name || "").toUpperCase().startsWith("DIRECT VEHICLE")) || null;
                    if (d) setDvrSupplier(d);
                }).catch(() => {});
        }
    };

    const calcPostage = (pc) => {
        setPostageBusy(true);
        setPostageRes(null);
        axios.post("/api/postage-quote", {
            postcode: typeof pc === "string" ? pc : postagePc,
            parts: (partOrder.parts || []).map(pt => ({part_number: pt.part_number, name: pt.name,
                                                      customer_cost: pt.customer_cost, is_postage: pt.is_postage}))
        })
            .then(res => setPostageRes(res.data))
            .catch(err => setPostageRes({detail: (err.response && err.response.data && err.response.data.detail) || "Could not calculate postage"}))
            .finally(() => setPostageBusy(false));
    };

    // One carriage line per quote: update it if there is one, else add it (staff can still edit it)
    const applyPostage = (amount, opt) => {
        // opt: the chosen delivery option for large items {carrier: FedEx|DVR, service} (default FedEx)
        const dvr = opt && opt.carrier === "DVR";
        const name = dvr ? "Carriage (DVR van)" : (opt && opt.service === "FEDEX_PRIORITY_FREIGHT" ? "Carriage (FedEx Freight)" : "Carriage (FedEx)");
        const supplier = dvr ? dvrSupplier : fedexSupplier;
        const vpTrack = (trackingOptions.results || []).find(t => (t.name || "").toLowerCase() === "van parts direct") || null;
        const parts = partOrder.parts ? [...partOrder.parts] : [];
        const i = parts.findIndex(pt => pt.is_postage);
        if (i >= 0) {
            parts[i] = {...parts[i], name, supplier: supplier || parts[i].supplier, cost: amount, customer_cost: amount, cost_override: true};
        } else {
            parts.push({name, part_number: "CARRIAGE", supplier,
                        supplier_tracking_option: vpTrack, customer_tracking_option: vpTrack,
                        cost: amount, customer_cost: amount, cost_override: true, is_postage: true,
                        from_stock: false, to_be_ordered: false, cost_vat: "INPUT2"});
        }
        triggerChange({parts: parts});
        setPostageOpen(false);
        toast.success("Carriage £" + amount.toFixed(2) + " added - remember to Save", {position: "top-right"});
    };

    const onSearchPartClick = () => {
        setPartsModalState({...partsModalState, modalOpen: true});
    };

    const onSearchWorkClick = () => {
        setWorkModalState({...workModalState, modalOpen: true});
    };

    const partLookupToParts = (lookup) => {
        let part = {
          part_number: lookup.part_number,
          cost: lookup.cost,
          customer_cost: lookup.customer_cost,
          supplier: lookup.supplier,
          name: lookup.name,
          to_be_ordered: true
        }
        if(lookup.customer_cost === 0) {
          part.customer_cost = getPartCustomerCost(part, partOrder.customer, appDefaults);
        }

        return part;
    };

    const searchSelectWork = (work, workIndex) => {
        // work index do not do anything but are passed to function because it is used somewhere else
        let parts = [];
        work.part_work_lookup.map(part => {
          parts.push(partLookupToParts(part))
        })
        let currentParts = partOrder.parts ? partOrder.parts : [];
        let newParts = currentParts.concat(parts)
        triggerChange({parts: newParts});

        setWorkModalState({...workModalState, modalOpen: false});
    };

    const getDisabled = () => {
        // Returns whether a field should be disabled or not
        if(partOrder.status === "COMPLETE"){
            return true
        }
        if (lockState && lockState.locked) {   // B6: someone else is editing this order
            return true
        }
        return false
    }

    const lookupVehicle = (mode, auto = false) => {
        // mode: "reg" -> lookup by customer_vehicle, "vin" -> lookup by customer_vehicle_vin.
        // Hits GET /api/vehicle-lookup (checkcardetails, cached) and shows the result.
        // auto: fired from leaving the reg box (C1) - stays quiet if there's nothing to do.
        const q = (mode === "vin" ? partOrder.customer_vehicle_vin : partOrder.customer_vehicle) || "";
        const query = q.trim();
        if (!query) {
            if (!auto) toast.error("Enter a " + (mode === "vin" ? "VIN" : "registration") + " first", {position: "top-right"});
            return;
        }
        setVehLookupLoading(true);
        setVehLookup(null);
        const params = mode === "vin" ? {vin: query} : {reg: query};
        axios.get("/api/vehicle-lookup", {params})
            .then(res => {
                const d = res.data || {};
                setVehLookup(d);
                if (!d.make) {
                    toast.warn("No vehicle details found", {position: "top-right"});
                    return;
                }
                // auto-fill the sibling field if the lookup returned it and it is blank
                if (!getDisabled()) {
                    const fill = {};
                    // Neil 9 Oct: a REG lookup REPLACES the VIN (it was keeping the previous reg's VIN
                    // when the reg was corrected). A VIN lookup only fills a blank reg.
                    if (d.vin && (mode === "reg" || !(partOrder.customer_vehicle_vin || "").trim())) fill.customer_vehicle_vin = d.vin;
                    if (d.reg && !(partOrder.customer_vehicle || "").trim()) fill.customer_vehicle = d.reg;
                    if (Object.keys(fill).length) triggerChange(fill);
                    if (fill.customer_vehicle_vin) {
                        toast.success("VIN filled" + (d.vin_source && d.vin_source !== "ukvd" ? " from our records" : "") +
                            (partOrder.id ? " - save the order to keep it" : ""), {position: "top-right"});
                    } else if (mode === "reg" && !d.vin && !(partOrder.customer_vehicle_vin || "").trim()) {
                        toast.info(d.vin_note || "No VIN found for this reg - please enter it", {position: "top-right"});
                    }
                }
            })
            .catch(err => {
                const msg = (err && err.response && err.response.data && err.response.data.detail) || "Vehicle lookup failed";
                toast.error(msg, {position: "top-right"});
            })
            .finally(() => setVehLookupLoading(false));
    }

    const loadVehPartsHistory = () => {
        // D5: parts previously fitted to this vehicle (reg/VIN), across jobs and part orders.
        const reg = (partOrder.customer_vehicle || "").toUpperCase().replace(/\s/g, "");
        const vin = (partOrder.customer_vehicle_vin || "").toUpperCase().replace(/\s/g, "");
        if (!reg && !vin) { toast.error("Enter a registration or VIN first", {position: "top-right"}); return; }
        axios.get("/api/vehicle-parts", {params: {reg, vin}})
            .then(r => setVehPartsHist((r.data && r.data.parts) || []))
            .catch(() => toast.error("Could not load parts history", {position: "top-right"}));
    }

    const FULFIL_STAGES = ["new", "awaiting_postage", "awaiting_reg_vin", "processing", "parts_awaiting_ordering", "awaiting_parts", "packing", "dispatched", "completed"];
    const STAGE_LABELS = {new: "New", awaiting_postage: "Awaiting Postage Payment", awaiting_reg_vin: "Awaiting Reg/VIN", parts_awaiting_ordering: "Parts Awaiting Ordering", processing: "Processing", awaiting_parts: "Awaiting Parts", packing: "Packing", dispatched: "Dispatched", completed: "Completed"};
    // Neil 9 Oct: a completed order that predates the fulfilment stages (or never moved past "new")
    // should read as Completed on the stepper, not New. Orders completed through the new Awaiting-Parts
    // flow keep their real stage so packing/dispatch still show.
    const currentStage = (partOrder.status === "COMPLETE" && (!partOrder.fulfilment_stage || partOrder.fulfilment_stage === "new"))
        ? "completed" : (partOrder.fulfilment_stage || "new");
    const currentStageIndex = FULFIL_STAGES.indexOf(currentStage);

    // Quote journey (Neil/Jason 9 Oct): a separate stepper shown while the order is still a QUOTE.
    // Derived from state: priced -> processing; emailed -> quote sent; accepted -> quote accepted;
    // pay link out -> awaiting payment. Paying it converts the quote to an order (handled server-side).
    const QUOTE_STAGES = [
        {k: "new_quote", label: "New Quote"}, {k: "processing", label: "Processing"},
        {k: "quote_sent", label: "Quote Sent"}, {k: "quote_accepted", label: "Quote Accepted"},
        {k: "awaiting_payment", label: "Awaiting Payment"}];
    const _hasPriced = (partOrder.parts || []).some(p => Number(p.customer_cost) > 0);
    // Use the stored quote_stage (clickable stepper); fall back to a sensible derived stage for
    // quotes that predate it.
    const _qStage = partOrder.quote_stage
        || ((partOrder.quote_accepted_at && (partOrder.pay_link_status === "sent" || partOrder.pay_link_status === "paid")) ? "awaiting_payment"
            : partOrder.quote_accepted_at ? "quote_accepted"
            : partOrder.quote_sent_at ? "quote_sent"
            : (_hasPriced ? "processing" : "new_quote"));
    const quoteStageIndex = QUOTE_STAGES.findIndex(s => s.k === _qStage);
    // Account customer = Van Parts credit limit > 0. They are invoiced on account, so they never get
    // the payment option (Neil/Jason 9 Oct).
    const isAccount = creditInfo ? !!creditInfo.is_account : !!(partOrder.customer && (partOrder.customer.van_parts_direct_credit_limit || 0) > 0);
    // Over-credit-limit flag (Neil/Jason 9 Oct): the order still goes through, but if an account
    // customer's order takes them past their Van Parts credit limit we warn in red before the parts.
    // Available credit = what Xero reports left when connected, otherwise the full limit.
    const _vpLimit = creditInfo ? Number(creditInfo.credit_limit || 0) : Number((partOrder.customer && partOrder.customer.van_parts_direct_credit_limit) || 0);
    const _vpAvail = (creditInfo && creditInfo.credit_left != null) ? Number(creditInfo.credit_left) : _vpLimit;
    const _orderIncVat = Number(partOrder.total_part_customer_charge_inc_vat || 0);
    const overCredit = isAccount && _vpLimit > 0 && _orderIncVat > _vpAvail;

    const setStage = (stage) => {
        if (!partOrder.id) { toast.error("Save the order first", {position: "top-right"}); return; }
        // D2: warn before moving an order BACKWARDS (it should normally only go forward).
        const _cur = FULFIL_STAGES.indexOf(partOrder.fulfilment_stage || "new");
        const _tgt = FULFIL_STAGES.indexOf(stage);
        if (_tgt > -1 && _cur > -1 && _tgt < _cur &&
            !window.confirm("This moves the order BACK to '" + (STAGE_LABELS[stage] || stage) + "'. Are you sure?")) {
            return;
        }
        const isVanParts = partOrder.department && partOrder.department.id === 9;
        const postStage = (notify_customer) =>
            axios.post("/api/partorder/" + partOrder.id + "/stage", {stage, num_boxes: partOrder.num_boxes || 1, notify_customer})
                .then(res => {
                    const d = res.data || {};
                    triggerChange({fulfilment_stage: d.fulfilment_stage, num_boxes: d.num_boxes});
                    loadEvents();
                    const ws = d.website_sync || {};
                    let extra = "";
                    if (ws.demo) extra = " (demo: website update simulated)";
                    else if (ws.synced) extra = " + website updated";
                    if (d.emailed && d.emailed.sent) extra += (d.emailed.invoice ? " + invoice emailed" : " + customer emailed");
                    toast.success("Stage: " + d.fulfilment_stage + extra, {position: "top-right"});
                    return d;
                });

        // Neil 8 Oct: for a Van Parts order, clicking "Awaiting Parts" both emails the invoice AND
        // completes the order (income/expense + the Rapid Fit cross-charge) in one go, so nobody has
        // to remember the old Complete button (hidden for Van Parts). Completion runs first, so it
        // still validates and NAMES any missing field; the fulfilment stages stay live afterwards for
        // packing and dispatch.
        if (stage === "awaiting_parts" && isVanParts) {
            if (!window.confirm("Move to Awaiting Parts?\n\nThis emails the invoice to the customer and COMPLETES the order (income + Rapid Fit charge). The parts and details lock, but you can still move the fulfilment stage for packing and dispatch.")) return;
            setStageBusy(true);
            axios.post(props.endpoint + "partorderapprove", partOrder)
                .then(() => postStage(!!orderEmail(partOrder)))
                .then(() => dispatch(createSetDataFreshAction(dataFresh + 1)))
                .catch(err => toast.error((err && err.response && err.response.data && err.response.data.detail) || "Could not complete the order", {position: "top-right"}))
                .finally(() => setStageBusy(false));
            return;
        }

        let notify_customer = false;
        if (stage === "awaiting_parts" && orderEmail(partOrder)) {
            notify_customer = window.confirm("Email the customer to let them know their parts are being ordered in?");
        }
        setStageBusy(true);
        postStage(notify_customer)
            .catch(() => toast.error("Could not update stage", {position: "top-right"}))
            .finally(() => setStageBusy(false));
    }

    const requestRegVin = () => {
        if (!partOrder.id) { toast.error("Save the order first", {position: "top-right"}); return; }
        setStageBusy(true);
        axios.post("/api/partorder/" + partOrder.id + "/request-reg-vin")
            .then(res => {
                const d = res.data || {};
                triggerChange({fulfilment_stage: d.stage || "awaiting_reg_vin"});
                loadEvents();
                const sent = d.email && d.email.sent;
                toast.success("Reg/VIN requested" + (sent ? " - email sent to the customer" : " (not emailed: no customer email on the order)"), {position: "top-right"});
            })
            .catch(() => toast.error("Could not request reg/VIN", {position: "top-right"}))
            .finally(() => setStageBusy(false));
    }

    // FedEx labels: orders only (never quotes/enquiries). Booking happens in the modal,
    // behind a "this is a chargeable shipment" confirmation. Reprint uses the stored label.
    // (Restored 13 Oct 2026 - the 9 Oct quote-journey commit had dropped this wiring.)
    const openFedexLabel = (replace) => {
        if (!partOrder.id) { toast.error("Save the order first", {position: "top-right"}); return; }
        setFedexReplace(!!replace);
        setFedexOpen(true);
    }

    const reprintFedexLabel = () => {
        setFedexBusy(true);
        axios.get("/api/partorder/" + partOrder.id + "/fedex-label/zpl")
            .then(async res => {
                const printed = await printZpl(res.data.zpl, "fedex_" + res.data.fedex_tracking + ".zpl");
                loadEvents();
                if (printed.how === "printed") toast.success("Label sent to " + (printed.printer.name || printed.printer.ip), {position: "top-right"});
                else toast.warn(printed.reason + " Downloaded as .zpl instead.", {position: "top-right"});
            })
            .catch(() => toast.error("Could not reprint the label", {position: "top-right"}))
            .finally(() => setFedexBusy(false));
    }

    // Ask FedEx for the latest scans now (the cron also does this every 30 minutes)
    const checkFedexTracking = () => {
        setTrackBusy(true);
        axios.post("/api/partorder/" + partOrder.id + "/fedex-track")
            .then(res => {
                const d = res.data;
                triggerChange({fedex_status: d.fedex_status, fedex_status_at: d.fedex_status_at,
                               fedex_delivered_at: d.fedex_delivered_at, fedex_last_checked_at: d.fedex_last_checked_at,
                               ...(d.fulfilment_stage ? {fulfilment_stage: d.fulfilment_stage} : {})});
                loadEvents();
                toast.success(d.new_events ? d.new_events + " new FedEx update(s)" : "No new FedEx updates", {position: "top-right"});
            })
            .catch(err => toast.error((err.response && err.response.data && err.response.data.detail) || "FedEx tracking check failed", {position: "top-right"}))
            .finally(() => setTrackBusy(false));
    }

    // 13 Oct (Neil): booking the label moves the order to Dispatched - the server does it, we mirror it
    const onFedexBooked = (d) => {
        triggerChange({label_printed: true, fedex_tracking: d.fedex_tracking, fedex_label_sandbox: d.label_sandbox,
                       num_boxes: d.num_boxes || partOrder.num_boxes,
                       ...(d.fulfilment_stage ? {fulfilment_stage: d.fulfilment_stage} : {})});
        loadEvents();
    }

    const loadEvents = () => {
        if (partOrder && partOrder.id) {
            axios.get("/api/partorder/" + partOrder.id + "/events")
                .then(r => setEvents((r.data && r.data.events) || []))
                .catch(() => {});
            // 13 Oct (Andrew): every action reloads the audit trail - refresh the suggested refund too,
            // it used to stay stale until the page was reloaded (e.g. after completing the order).
            loadSuggestion();
        }
    }

    const loadSuggestion = () => {
        if (partOrder && partOrder.id) {
            axios.get("/api/partorder/" + partOrder.id + "/refund")
                .then(r => setRefundSuggestion(r.data || null))
                .catch(() => {});
        }
    }

    useEffect(() => {
        axios.get("/api/refund-permission")
            .then(r => setCanRefund(!!(r.data && r.data.can_refund)))
            .catch(() => {});
    }, []);

    const _partsKey = (partOrder && Array.isArray(partOrder.parts))
        ? partOrder.parts.map(p => [p.id, p.cost, p.customer_cost, p.quantity, p.is_postage].join(":")).join("|") : "";
    useEffect(() => {
        loadEvents();
    }, [partOrder && partOrder.id]);
    useEffect(() => {
        loadSuggestion();
    }, [partOrder && partOrder.id, partOrder && partOrder.status, partOrder && partOrder.fulfilment_stage,
        partOrder && partOrder.refunded, partOrder && partOrder.customer_paid_amount, _partsKey]);

    // C1: an order opened with a reg but no VIN (e.g. a website order) looks the VIN up once.
    // Lookups are saved per reg, so re-opening the order never costs a second lookup.
    const autoVinTried = useRef(null);
    useEffect(() => {
        if (!props.loaded || !partOrder || !partOrder.id || autoVinTried.current === partOrder.id) return;
        autoVinTried.current = partOrder.id;
        const reg = (partOrder.customer_vehicle || "").replace(/\s/g, "");
        if (reg.length >= 5 && !(partOrder.customer_vehicle_vin || "").trim() && !getDisabled()) {
            lookupVehicle("reg", true);
        }
    }, [props.loaded, partOrder && partOrder.id]);

    // B6: soft one-editor lock - acquire on open, heartbeat, release on leaving.
    const autoProcessed = useRef(null);
    const [lockState, setLockState] = useState(null);
    const takeControl = () => {
        if (partOrder.id) axios.post("/api/partorder/" + partOrder.id + "/lock", {action: "takeover"})
            .then(r => setLockState(r.data)).catch(() => {});
    };
    useEffect(() => {
        if (!partOrder || !partOrder.id) return;
        let alive = true; const oid = partOrder.id;
        const call = (a) => axios.post("/api/partorder/" + oid + "/lock", {action: a})
            .then(r => { if (alive) setLockState(r.data); }).catch(() => {});
        call("acquire");
        const hb = setInterval(() => call("heartbeat"), 60000);
        return () => { alive = false; clearInterval(hb);
            axios.post("/api/partorder/" + oid + "/lock", {action: "release"}).catch(() => {}); };
    }, [partOrder && partOrder.id]);

    // D2: a brand-new ORDER auto-advances to Processing when opened.
    useEffect(() => {
        if (!props.loaded || !partOrder || !partOrder.id || autoProcessed.current === partOrder.id) return;
        autoProcessed.current = partOrder.id;
        if (partOrder.status === "ORDER" && (partOrder.fulfilment_stage || "new") === "new") {
            setStage("processing");
        }
    }, [props.loaded, partOrder && partOrder.id]);

    const doRefund = () => {
        if (!partOrder.id) { toast.error("Save the order first", {position: "top-right"}); return; }
        let payload;
        let amountLabel;
        if (refundMode === "rule") {
            const amt = refundSuggestion && refundSuggestion.suggested;
            if (!(amt > 0)) { toast.error("No suggested refund available", {position: "top-right"}); return; }
            payload = {mode: "rule"};
            amountLabel = "£" + Number(amt).toFixed(2) + " (" + (refundSuggestion.basis || "rule-based") + ")";
        } else {
            const v = parseFloat(refundValue);
            if (!(v > 0)) { toast.error("Enter a refund " + (refundMode === "percent" ? "percentage" : "amount"), {position: "top-right"}); return; }
            payload = {mode: refundMode, value: v};
            amountLabel = refundMode === "percent" ? (v + "% of the part price") : ("£" + v.toFixed(2));
        }
        payload.reason = partOrder.description || "Refund via order screen";
        if (!window.confirm("Refund " + amountLabel + "?\n\nThis refunds the customer via Worldpay and credits Van Parts Direct (Rapid Fit retains 10%). This cannot be undone.")) return;
        setRefundBusy(true);
        axios.post("/api/partorder/" + partOrder.id + "/refund", payload)
            .then(res => {
                const d = res.data || {};
                setRefundResult(d);
                if (d.demo) toast.info("Demo: refund simulated - no real money moved (customer £" + d.customer_refund + ")", {position: "top-right"});
                else if (d.refunded) { toast.success("Refunded £" + d.customer_refund + " to the customer", {position: "top-right"}); triggerChange({refunded: true, refund_amount: d.customer_refund}); }
                else toast.warn("Refund not completed: " + ((d.worldpay && d.worldpay.detail) || "see details"), {position: "top-right"});
                loadEvents();
            })
            .catch(err => {
                const msg = (err && err.response && err.response.data && err.response.data.detail) || "Refund failed";
                toast.error(msg, {position: "top-right"});
            })
            .finally(() => setRefundBusy(false));
    }

    const doConvertToOrder = () => {
        if (!partOrder.id) return;
        if (!window.confirm("Convert this quote to an order?\n\nFor account customers - they will be invoiced on account.")) return;
        setStageBusy(true);
        axios.post("/api/partorder/" + partOrder.id + "/convert-to-order", {})
            .then(() => { toast.success("Converted to order", {position: "top-right"}); dispatch(createSetDataFreshAction(dataFresh + 1)); })
            .catch(err => toast.error((err.response && err.response.data && err.response.data.detail) || "Could not convert to order", {position: "top-right"}))
            .finally(() => setStageBusy(false));
    };

    const setQuoteStage = (stage) => {
        if (!partOrder.id) { toast.error("Save the quote first", {position: "top-right"}); return; }
        setStageBusy(true);
        axios.post("/api/partorder/" + partOrder.id + "/quote-stage", {stage})
            .then(r => {
                const d = r.data || {};
                triggerChange({quote_stage: d.quote_stage || stage, quote_sent_at: d.quote_sent_at || partOrder.quote_sent_at});
                loadEvents();
                toast.success("Quote: " + ((QUOTE_STAGES.find(s => s.k === stage) || {}).label || stage), {position: "top-right"});
            })
            .catch(() => toast.error("Could not update the quote stage", {position: "top-right"}))
            .finally(() => setStageBusy(false));
    };

    const doSendPaymentLink = () => {
        if (!partOrder.id) { toast.error("Save the quote first", {position: "top-right"}); return; }
        // Neil 9 Oct: always confirm before sending a payment link so it can't be fired by mistake
        // (e.g. for something already paid). Account customers get an extra note that they skip it.
        if (partOrder.customer && (partOrder.customer.van_parts_direct_credit_limit || 0) > 0) {
            if (!window.confirm("This is an ACCOUNT customer (invoiced on account - they normally skip the payment link). Send a payment link anyway?")) return;
        } else if (!window.confirm("Email a payment link to the customer for this order?\n\nDon't send one if it has already been paid.")) {
            return;
        }
        setPayBusy(true);
        axios.post("/api/partorder/" + partOrder.id + "/payment-link", {})
            .then(r => { setPayLink(r.data); toast.success("Payment link created", {position: "top-right"}); loadEvents(); })
            .catch(err => toast.error((err.response && err.response.data && err.response.data.detail) || "Could not create link", {position: "top-right"}))
            .finally(() => setPayBusy(false));
    }

    // 14 Oct (Neil): postage quoted after the order (website "large item - delivery quoted
    // separately"). The customer gets a pay link for just the delivery; when they pay, the server
    // adds the carriage line and adds the payment to the amount paid.
    const postageReq = (action, extra) => {
        if (!partOrder.id) { toast.error("Save the order first", {position: "top-right"}); return; }
        setPostageReqBusy(true);
        axios.post("/api/partorder/" + partOrder.id + "/postage-request", {action, ...(extra || {})})
            .then(r => {
                const {full_url, emailed, ...state} = r.data;
                triggerChange(state);
                setPostageReqOpen(false);
                if (action === "request") {
                    toast.success(emailed && emailed.sent ? "Postage request emailed to the customer" : "Postage link created - the email did NOT send, copy the link from the audit trail", {position: "top-right"});
                } else {
                    toast.success(action === "flag" ? "Marked as needing a delivery quote" : "Postage request cleared", {position: "top-right"});
                }
                loadEvents();
            })
            .catch(err => toast.error((err.response && err.response.data && err.response.data.detail) || "Could not update the postage", {position: "top-right"}))
            .finally(() => setPostageReqBusy(false));
    };

    const sendPostageRequest = () => {
        const amt = Number(postageReqAmount);
        if (!amt || amt <= 0) { toast.error("Enter the delivery charge (ex VAT)", {position: "top-right"}); return; }
        if (!window.confirm("Email the customer a payment link for delivery of £" + amt.toFixed(2) + " + VAT (£" + (amt * 1.2).toFixed(2) + ")?")) return;
        postageReq("request", {amount: amt, carrier: postageReqCarrier});
    };

    const postagePanel = () => {
        if (!partOrder.id || !["ORDER", "COMPLETE"].includes(partOrder.status)) return null;
        const ps = partOrder.postage_status || "";
        const active = ["to_quote", "requested", "paid"].includes(ps);
        if (!active && !postageReqOpen) return null;
        const money = v => "£" + Number(v || 0).toFixed(2);
        const when = d => d ? new Date(d).toLocaleString("en-GB", {day: "numeric", month: "short", hour: "2-digit", minute: "2-digit"}) : "";
        const cls = ps === "paid" ? "is-success" : (ps === "requested" ? "is-warning" : "is-danger");
        return (
            <div className={"notification " + cls + (ps === "paid" ? " is-light" : "")}>
                {ps === "to_quote" ? <p><strong>DELIVERY TO BE QUOTED</strong> - {partOrder.postage_note || "this order has no carriage"}. Don't order the parts until the customer has paid for the delivery.</p> : null}
                {ps === "requested" ? <p><strong>AWAITING POSTAGE PAYMENT</strong> - {money(partOrder.postage_amount)} + VAT ({money(partOrder.postage_link_amount)}) requested {when(partOrder.postage_requested_at)}. {partOrder.postage_note}</p> : null}
                {ps === "paid" ? <p><strong>POSTAGE PAID</strong> - {money(partOrder.postage_link_amount)} paid {when(partOrder.postage_paid_at)}; the carriage line was added to the order (reload to see it).</p> : null}
                {!active ? <p><strong>Postage request</strong> - email the customer a payment link for the delivery only.</p> : null}
                {ps !== "paid" ?
                    <div className="field is-grouped is-grouped-multiline" style={{marginTop: "0.6rem"}}>
                        <div className="control">
                            <input className="input" type="number" step="0.01" min="0" style={{width: "150px"}}
                                   placeholder="£ ex VAT" value={postageReqAmount}
                                   onChange={e => setPostageReqAmount(e.target.value)}/>
                        </div>
                        <div className="control">
                            <div className="select">
                                <select value={postageReqCarrier} onChange={e => setPostageReqCarrier(e.target.value)}>
                                    <option>FedEx</option>
                                    <option>DVR</option>
                                    <option>FedEx Freight</option>
                                    <option>Other</option>
                                </select>
                            </div>
                        </div>
                        <div className="control">
                            <button type="button" className={"button is-link" + (postageReqBusy ? " is-loading" : "")}
                                    onClick={sendPostageRequest} disabled={postageReqBusy}>
                                {ps === "requested" ? "Re-send postage request" : "Send postage request"}
                            </button>
                        </div>
                        <div className="control">
                            <button type="button" className={"button" + (postageBusy ? " is-loading" : "")} disabled={postageBusy}
                                    onClick={() => { const pc = postagePc || guessPostcode(); setPostagePc(pc); calcPostage(pc); }}>
                                Calculate delivery
                            </button>
                        </div>
                        {active ?
                            <div className="control">
                                <button type="button" className="button is-light" disabled={postageReqBusy}
                                        onClick={() => { const n = window.prompt("No postage to take - why? (e.g. customer collecting, own courier)"); if (n !== null) postageReq("clear", {note: n}); }}>
                                    No postage needed
                                </button>
                            </div>
                            : <div className="control">
                                <button type="button" className="button is-light" onClick={() => setPostageReqOpen(false)}>Cancel</button>
                            </div>}
                    </div> : null}
                {postageRes && ps !== "paid" ?
                    <div className="is-size-7" style={{marginTop: "0.4rem"}}>
                        {postageRes.detail ? <span>{postageRes.detail}</span> : null}
                        {postageRes.quote_required ? <span><strong>{postageRes.collect_only ? "Collect / own courier only: " : "Can't price automatically: "}</strong>{(postageRes.reasons || []).join("; ")}</span> : null}
                        {!postageRes.quote_required && (postageRes.options || []).length ?
                            [...postageRes.options].sort((a, b) => a.charge - b.charge).map((o, k) =>
                                <div key={k}>{o.label} to {postageRes.postcode}: <strong>{money(o.charge)}</strong> + VAT{o.best ? " (cheapest)" : ""} -{" "}
                                    <a onClick={() => { setPostageReqAmount(String(o.charge)); setPostageReqCarrier(o.carrier === "DVR" ? "DVR" : (o.service === "FEDEX_PRIORITY_FREIGHT" ? "FedEx Freight" : "FedEx")); }}>use this</a>
                                </div>) : null}
                        {postageRes.error ? <span className="has-text-danger">{postageRes.error}</span> : null}
                    </div> : null}
            </div>);
    };

    const doSendMessage = () => {
        if (!partOrder.id) { toast.error("Save the order first", {position: "top-right"}); return; }
        const msg = (messageText || "").trim();
        if (!msg) { toast.error("Type a message first", {position: "top-right"}); return; }
        if (!window.confirm("Send this message to the customer?")) return;
        setMessageBusy(true);
        axios.post("/api/partorder/" + partOrder.id + "/message", {message: msg})
            .then(() => { toast.success("Message sent - order is now awaiting a reply", {position: "top-right"}); setMessageText(""); triggerChange({awaiting_reply: true}); loadEvents(); })
            .catch(err => toast.error((err.response && err.response.data && err.response.data.detail) || "Could not send message", {position: "top-right"}))
            .finally(() => setMessageBusy(false));
    }

    const doEmailQuote = () => {
        if (!partOrder.id) { toast.error("Save the quote first", {position: "top-right"}); return; }
        axios.post("/api/partorder/" + partOrder.id + "/email-quote", {})
            .then(r => {
                toast.success("Quote emailed to the customer (£" + (r.data.total || "") + ")", {position: "top-right"});
                // Emailing the quote advances the journey to Quote Sent - reflect it on the stepper now.
                triggerChange({quote_stage: "quote_sent", quote_sent_at: partOrder.quote_sent_at || new Date().toISOString()});
                loadEvents();
            })
            .catch(err => toast.error((err.response && err.response.data && err.response.data.detail) || "Could not email quote", {position: "top-right"}));
    }

    const doQuoteCalc = (apply) => {
        if (!partOrder.id) { toast.error("Save the quote first", {position: "top-right"}); return; }
        axios.post("/api/partorder/" + partOrder.id + "/quote-calc",
            {margin_pct: qcMargin || 0, labour_hours: qcHours || 0, labour_rate: qcRate || 0, apply: !!apply})
            .then(r => {
                setQcResult(r.data);
                if (apply) { toast.success("Applied to " + r.data.applied + " part(s) - reopen the order to see the new prices", {position: "top-right"}); loadEvents(); }
            })
            .catch(() => toast.error("Could not calculate quote", {position: "top-right"}));
    }

    const doResendInvoice = () => {
        if (!window.confirm("Re-email the invoice to the customer?")) return;
        axios.post("/api/partorder/" + partOrder.id + "/resend-invoice", {})
            .then(() => { toast.success("Invoice re-sent to the customer", {position: "top-right"}); loadEvents(); })
            .catch(err => toast.error((err.response && err.response.data && err.response.data.detail) || "Could not resend invoice", {position: "top-right"}));
    }

    const [statusOption, statusOptionInput] = useButtonSelect({
        label: "Status",
        initialValue: partOrder.status ? partOrder.status : (partOrder.id ? "ORDER" : "QUOTE"),
        prop: "status",
        options: options,
        callback: onChange,
        disabled: getDisabled(),
        loaded: props.loaded
    });

    const addPart = () => {
        let newPart = {
            name: "",
            cost: 0,
            customer_cost: 0,
            supplier: null,
            date_ordered: null,
            part_number: "",
            invoice_number: "",
            comments: "",
            cost_override: false,
            from_stock: false,
            backorder_expected_date: null,
            to_be_ordered: true,
            cost_vat: "INPUT2"
        }
        let currentParts = partOrder.parts ? partOrder.parts : [];
        currentParts.push(newPart);
        triggerChange({parts: currentParts});
    }

    const addCashSale = () => {
        let newCashSale = {
            name: "",
            email: "",
            phone: "",
            address :"",
            contact: partOrder.customer.id
        };
        partOrder.cash_sale.push(newCashSale);
        triggerChange({cash_sale: partOrder.cash_sale})
    }

    const onCopyContactInfo = () => {
        if(partOrder.customer && partOrder.customer.id){
            // this could be 1 function call
            triggerChange({delivery_address: partOrder.customer.address_line1});
            triggerChange({delivery_contact_email: partOrder.customer.company_email});
            triggerChange({delivery_phone: partOrder.customer.company_phone});
        }
    }

    const onCashSaleChange = (e, index) => {
        let cashSale = {...partOrder.cash_sale[index]}
        cashSale[e.target.name] = e.target.value;
        partOrder.cash_sale[index] = cashSale;
        triggerChange({cash_sale: partOrder.cash_sale});
    }

    if(!props.loaded){
        return <LoadingSpinner/>
    }

    const departmentObjects = departments.results.reduce((obj, item) => {
        return {
          ...obj,
          [item["id"]]: item
        };
    }, {});

    const trackingOptionObjects = trackingOptions.results.reduce((obj, item) => {
        return {
          ...obj,
          [item["id"]]: item
        };
    }, {});

    return (
        <div>
            {lockState && lockState.locked ?
                <div className="notification is-warning">
                    This order is being edited by <strong>{lockState.locked_by}</strong>.{" "}
                    <button className="button is-small" onClick={takeControl}>Take control</button>
                </div> : null}
            {partOrder.customer && partOrder.customer.id ? (() => {
                const ci = creditInfo || {};
                const acct = (ci.is_account != null) ? ci.is_account : (Number(partOrder.customer.van_parts_direct_credit_limit || 0) > 0);
                const limit = Number((ci.credit_limit != null) ? ci.credit_limit : (partOrder.customer.van_parts_direct_credit_limit || 0));
                const owed = Number(ci.owed || 0);
                const left = Number((ci.credit_left != null) ? ci.credit_left : limit);
                return (
                <div className={"notification " + (acct ? "is-info is-light" : "is-light")}>
                    <strong>{acct ? "ACCOUNT CUSTOMER" : "CASH CUSTOMER"}</strong>
                    {acct ? (
                        <span> — credit limit £{limit.toFixed(2)}, owed £{owed.toFixed(2)}, remaining £{left.toFixed(2)}
                            {ci.source === "xero" ? " (balance from Xero)" : (ci.source === "app" ? " (balance from our records)" : "")}. Invoiced on account — no payment link needed.</span>
                    ) : (
                        <span> — pays up front by card / payment link.</span>
                    )}
                    <span className="has-text-grey" style={{display: "block", fontSize: "12px"}}>
                        Status is set by the customer's Van Parts credit limit and can only be changed on the customer's account page.
                    </span>
                </div>);
            })() : null}
            {postagePanel()}
            <br/>
            <br/>
            <Columns isMultiline>
                <Column>
                    <Columns isMultiline>
                        <Column>
                            <ContactSection
                                title={"Customer"}
                                endpoint={props.endpoint+"contacts"}
                                contact={partOrder.customer || {}}
                                type={"CC"}
                                disabled={getDisabled()}
                                contactUpdate={customer => triggerChange({customer})}
                            />
                        </Column>
                    </Columns>
                    {(partOrder.customer && partOrder.customer.is_cash_sale) &&
                        <Box>
                            <Columns isMultiline>
                                <Column>
                                    <Title>Cash Sale</Title>
                                </Column>
                                {partOrder.cash_sale.length == 0 &&
                                <Column>
                                    <Button
                                        color={"success"}
                                        onClick={() => addCashSale()}
                                    >
                                        Add Cash Sale +
                                    </Button>
                                </Column>
                                }
                            </Columns>
                        </Box>
                    }
                    {(partOrder.customer && partOrder.customer.is_cash_sale) &&
                        partOrder.cash_sale.map((sale, index) => (
                            <Columns isMultiline>
                                <Column>
                                    <CashSaleSection
                                        data={sale}
                                        index={index}
                                        addCashSale={addCashSale}
                                        onChange={onCashSaleChange}
                                    />
                                </Column>
                            </Columns>
                        ))
                    }
                </Column>
                <Column>
                    <Box>
                        <Columns isMultiline>
                            <Column>
                                <label className="label">Delivery Address</label>
                                <Input
                                    type={"text"}
                                    name={"delivery_address"}
                                    onChange={onChange}
                                    disabled={getDisabled()}
                                    value={partOrder.delivery_address}
                                />
                            </Column>
                        </Columns>
                        <Columns isMultiline>
                            <Column>
                                <label className="label">Delivery Email</label>
                                <Input
                                    type={"email"}
                                    name={"delivery_contact_email"}
                                    onChange={onChange}
                                    disabled={getDisabled()}
                                    value={partOrder.delivery_contact_email}
                                />
                            </Column>
                            <Column>
                                <label className="label">Delivery Phone Number</label>
                                <Input
                                    type={"email"}
                                    name={"delivery_phone"}
                                    onChange={onChange}
                                    disabled={getDisabled()}
                                    value={partOrder.delivery_phone}
                                />
                            </Column>
                        </Columns>
                        <Columns isMultiline>
                            <Column/>
                        </Columns>
                        <Columns isMultiline>
                            <Column/>
                        </Columns>
                        <Columns isMultiline>
                            <Column/>
                        </Columns>
                        <Columns isMultiline>
                            <Column/>
                        </Columns>
                        <Columns isMultiline>
                            <Column>
                                <Button
                                    color={"info"}
                                    onClick={onCopyContactInfo}
                                    disabled={getDisabled() || partOrder.status !== "ORDER"}
                                >
                                    Copy Contact Details
                                </Button>
                            </Column>
                        </Columns>
                    </Box>
                </Column>
            </Columns>
            <Box>
                <Columns isMultiline>
                    <Column>
                        <label className="label">Customer Vehicle Registration</label>
                        <Input
                            type={"text"}
                            name={"customer_vehicle"}
                            value={partOrder.customer_vehicle}
                            disabled={getDisabled()}
                            onChange={(e) => {
                                // B1: editing the reg to a different value clears the stale
                                // vehicle panel so the fresh lookup (on blur) fully repopulates.
                                onChange(e);
                                const r = (e.target.value || "").toUpperCase().replace(/\s/g, "");
                                if (vehLookup && (vehLookup.reg || "") !== r) setVehLookup(null);
                            }}
                            onBlur={() => {
                                // C1: leaving the reg box runs the lookup and fills the VIN
                                const reg = (partOrder.customer_vehicle || "").replace(/\s/g, "");
                                const done = vehLookup && (vehLookup.reg || "") === reg.toUpperCase();
                                if (reg.length >= 5 && !done && !vehLookupLoading) lookupVehicle("reg", true);
                            }}
                        />
                        <Button
                            color={"info"}
                            size={"small"}
                            style={{marginTop: "6px"}}
                            onClick={() => lookupVehicle("reg")}
                            disabled={vehLookupLoading}
                        >
                            {vehLookupLoading ? "Looking up..." : "Lookup by Reg"}
                        </Button>
                    </Column>
                    <Column>
                        <label className="label">Customer Vehicle Vin</label>
                        <Input
                            type={"text"}
                            name={"customer_vehicle_vin"}
                            value={partOrder.customer_vehicle_vin}
                            disabled={getDisabled()}
                            onChange={onChange}
                        />
                        <Button
                            color={"info"}
                            size={"small"}
                            style={{marginTop: "6px"}}
                            onClick={() => lookupVehicle("vin")}
                            disabled={vehLookupLoading}
                        >
                            {vehLookupLoading ? "Looking up..." : "Lookup by VIN"}
                        </Button>
                    </Column>
                    <Column>
                        <label className="label">Order Number</label>
                        <Input
                            type={"text"}
                            name={"order_number"}
                            value={partOrder.order_number}
                            disabled={getDisabled()}
                            onChange={onChange}
                        />
                    </Column>
                    <Column>
                        <label className="label">Invoice Number</label>
                        <Input
                            type={"text"}
                            name={"invoice_number"}
                            disabled={getDisabled()}
                            onChange={onChange}
                            value={partOrder.invoice_number}
                        />
                    </Column>
                    <Column>
                        {/* B4: order date (read-only) */}
                        <label className="label">Order Date</label>
                        <p className="is-size-6" style={{paddingTop: "6px"}}>
                            {partOrder.date_created ? new Date(partOrder.date_created).toLocaleDateString("en-GB")
                                : (partOrder.date_time_created ? new Date(partOrder.date_time_created).toLocaleDateString("en-GB") : "—")}
                        </p>
                        {/* D3: account/contract customer flag */}
                        {partOrder.customer && partOrder.customer.id ?
                            <span className={"tag " + (((partOrder.customer.van_parts_direct_credit_limit || 0) > 0) ? "is-info" : "is-light")} style={{marginTop: "4px"}}>
                                {((partOrder.customer.van_parts_direct_credit_limit || 0) > 0) ? "Account customer" : "Cash customer"}</span> : null}
                    </Column>
                    {/*<Columns.Column>*/}
                    {/*    <Form.Label>Date Paid</Form.Label>*/}
                    {/*    <Form.Input*/}
                    {/*        type={"date"}*/}
                    {/*        name={"date_paid"}*/}
                    {/*        value={partOrder.date_paid}*/}
                    {/*        disabled={true}*/}
                    {/*        onChange={onChange}*/}
                    {/*    />*/}
                    {/*</Columns.Column>*/}
                    <Column>
                        <DepartmentPicker
                            departments={departments}
                            onDepartmentChange={onDepartmentChange}
                            selectedDepartment={partOrder.department ? partOrder.department.id : 0}
                            showAll={false}
                            disabled={getDisabled()}
                        />
                    </Column>
                    {statusOptionInput}
                    <Column>
                        <label className="label">Enquiry Creator</label>
                        <Input
                            name={"creator"}
                            disabled={true}
                            value={partOrder.creator ? partOrder.creator.name : ""}
                        />
                    </Column>
                </Columns>
                <Columns isMultiline>
                    <Column>
                        <label className="label">Description</label>
                        <TextArea
                            name={"description"}
                            value={partOrder.description}
                            onChange={onChange}
                            disabled={getDisabled()}
                        />
                    </Column>
                </Columns>
                {vehLookup && vehLookup.make ?
                    <Columns isMultiline>
                        <Column>
                            <Box className={"has-background-info-light"}>
                                <p>
                                    <strong>Vehicle: {vehLookup.make} {vehLookup.model}</strong>
                                    {vehLookup.year ? " (" + vehLookup.year + ")" : ""}
                                    {vehLookup.is_maxus ? " — MAXUS / LDV" : ""}
                                </p>
                                <p>
                                    {vehLookup.fuel ? "Fuel: " + vehLookup.fuel + "   " : ""}
                                    {vehLookup.colour ? "Colour: " + vehLookup.colour + "   " : ""}
                                    {vehLookup.engine_cc ? "Engine: " + vehLookup.engine_cc + "cc   " : ""}
                                    {vehLookup.reg ? "Reg: " + vehLookup.reg + "   " : ""}
                                    {vehLookup.vin ? "VIN: " + vehLookup.vin : ""}
                                </p>
                                <p>
                                    {vehLookup.mot_status ? "MOT: " + vehLookup.mot_status + (vehLookup.mot_due ? " (due " + vehLookup.mot_due + ")" : "") + "   " : ""}
                                    {vehLookup.tax_status ? "Tax: " + vehLookup.tax_status + (vehLookup.tax_due ? " (due " + vehLookup.tax_due + ")" : "") : ""}
                                </p>
                            </Box>
                        </Column>
                    </Columns>
                    : null}
                <div style={{marginTop: "8px"}}>
                    <Button color={"light"} size={"small"} onClick={loadVehPartsHistory}>
                        Parts previously fitted to this vehicle
                    </Button>
                    {vehPartsHist ?
                        <Box className={"has-background-white-ter"} style={{marginTop: "8px"}}>
                            {vehPartsHist.length === 0 ?
                                <p className="has-text-grey">No parts recorded against this registration or VIN yet.</p>
                                :
                                <>
                                    <p style={{marginBottom: "6px"}}><strong>{vehPartsHist.length}</strong> part(s) previously fitted to this vehicle:</p>
                                    <table className="table is-fullwidth is-narrow is-size-7">
                                        <thead><tr><th>Date</th><th>Part</th><th>Number</th><th>Supplier</th><th>Order</th><th style={{textAlign: "right"}}>Cost</th></tr></thead>
                                        <tbody>
                                            {vehPartsHist.map((p, i) =>
                                                <tr key={i}>
                                                    <td>{p.order_date ? new Date(p.order_date).toLocaleDateString("en-GB") : (p.date ? new Date(p.date).toLocaleDateString("en-GB") : "")}</td>
                                                    <td>{p.name}</td>
                                                    <td>{p.part_number}</td>
                                                    <td>{p.supplier}</td>
                                                    <td>{p.order_id
                                                        ? <a href={"/editpartorder/" + p.order_id} target="_blank" rel="noreferrer">{p.order_number || ("#" + p.order_id)}</a>
                                                        : (p.job_id ? ("Job " + p.job_id) : "")}</td>
                                                    <td style={{textAlign: "right"}}>{p.cost != null ? "£" + Number(p.cost).toFixed(2) : ""}</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </>
                            }
                        </Box>
                        : null}
                </div>
            </Box>
            {partOrder.status !== "QUOTE" ?
            <Box>
                <Title>Fulfilment</Title>
                <Columns isMultiline>
                    <Column>
                        <label className="label">Stage</label>
                        <div className="buttons has-addons">
                            {FULFIL_STAGES.map((s, i) =>
                                <Button
                                    key={s}
                                    onClick={() => setStage(s)}
                                    disabled={stageBusy || (lockState && lockState.locked)}
                                    color={i < currentStageIndex ? "light" : (i === currentStageIndex ? "success" : "info")}
                                >
                                    {(i < currentStageIndex ? "✓ " : "") + (STAGE_LABELS[s] || s)}
                                </Button>
                            )}
                        </div>
                        <Button color="danger" onClick={requestRegVin}
                            disabled={stageBusy || getDisabled()} style={{marginTop: "8px"}}>
                            Request Reg/VIN from customer
                        </Button>
                    </Column>
                </Columns>
                <Columns isMultiline>
                    <Column>
                        <label className="label">Number of Boxes</label>
                        <Input
                            type={"number"}
                            name={"num_boxes"}
                            value={partOrder.num_boxes || 1}
                            min={1}
                            disabled={getDisabled()}
                            onChange={onChange}
                        />
                    </Column>
                    {(partOrder.status === "ORDER" || partOrder.status === "COMPLETE") ?
                    <Column>
                        <label className="label">FedEx Dispatch Label</label>
                        {partOrder.fedex_tracking ?
                            <>
                                <div className="buttons">
                                    <Button color={"warning"} onClick={reprintFedexLabel} disabled={fedexBusy}>
                                        {fedexBusy ? "Printing..." : "Reprint FedEx Label"}
                                    </Button>
                                    {/* 13 Oct: Awaiting Parts auto-completes, so packed orders are often COMPLETE */}
                                    {(partOrder.status === "ORDER" || partOrder.status === "COMPLETE") ?
                                        <Button color={"light"} onClick={() => openFedexLabel(true)} disabled={fedexBusy}>
                                            Book replacement
                                        </Button> : null}
                                </div>
                                <p style={{marginTop: "6px"}}>
                                    Tracking: <a href={fedexTrackUrl(partOrder.fedex_tracking)} target="_blank" rel="noopener noreferrer">{partOrder.fedex_tracking}</a>{partOrder.fedex_label_sandbox ? " (TEST)" : ""}
                                </p>
                                <p>
                                    FedEx status: {partOrder.fedex_delivered_at
                                        ? "Delivered " + new Date(partOrder.fedex_delivered_at).toLocaleString()
                                        : (partOrder.fedex_status || "awaiting first scan")}
                                    {partOrder.fedex_last_checked_at ?
                                        <span className="has-text-grey"> (checked {new Date(partOrder.fedex_last_checked_at).toLocaleString()})</span> : null}
                                    {" "}
                                    {!partOrder.fedex_delivered_at ?
                                        <Button className="is-small" color="light" disabled={trackBusy} onClick={checkFedexTracking}>
                                            {trackBusy ? "Checking..." : "Check now"}
                                        </Button> : null}
                                </p>
                                <p className="help">Every FedEx scan is recorded in the audit trail below.</p>
                            </> :
                            <Button
                                color={"warning"}
                                onClick={() => openFedexLabel(false)}
                                disabled={!(partOrder.status === "ORDER" || partOrder.status === "COMPLETE") || currentStageIndex < FULFIL_STAGES.indexOf("packing")}
                            >
                                Create FedEx Label
                            </Button>}
                        {!partOrder.fedex_tracking && currentStageIndex < FULFIL_STAGES.indexOf("packing") ?
                            <p style={{marginTop: "6px"}} className="has-text-grey">Available from the Packing stage</p> : null}
                        <div style={{marginTop: "8px"}}>
                            <Button className="is-small" color="light" onClick={() => setPrinterOpen(true)}>Label printer setup</Button>
                        </div>
                    </Column>
                    : null}
                </Columns>
            </Box>
            : null}
            {partOrder.status === "QUOTE" ?
                <Box>
                    <Title>Quote Journey</Title>
                    <div className="buttons has-addons">
                        {QUOTE_STAGES.map((qs, i) =>
                            <Button key={qs.k} onClick={() => setQuoteStage(qs.k)}
                                disabled={stageBusy || (lockState && lockState.locked)}
                                color={i <= quoteStageIndex ? "success" : "info"}>
                                {(i <= quoteStageIndex ? "✓ " : "") + qs.label + (qs.k === "quote_sent" && partOrder.quote_sent_at ? " (" + new Date(partOrder.quote_sent_at).toLocaleDateString() + ")" : "")}
                            </Button>
                        )}
                    </div>
                    {(partOrder.customer && (partOrder.customer.van_parts_direct_credit_limit || 0) > 0) ?
                        <Button color="success" style={{marginTop: "10px"}} onClick={doConvertToOrder} disabled={stageBusy}>
                            Convert to order (account customer)
                        </Button>
                        : <p className="has-text-grey" style={{marginTop: "8px", fontSize: "13px"}}>This quote becomes an order automatically once the customer pays.</p>}
                </Box>
                : null}
            {partOrder.status === "QUOTE" ?
                <Box>
                    <Title>Send quote</Title>
                    {isAccount ?
                        <p className="has-text-grey" style={{marginBottom: "8px"}}>Account customer - invoiced on account, so there is no payment link. Email the quote; it becomes an order when you convert it (or they accept).</p>
                        : <p className="has-text-grey" style={{marginBottom: "8px"}}>Email the quote - when the customer accepts it they are sent a Worldpay payment link, and paying it converts the quote to an order automatically.</p>}
                    {/* 12-Oct-2026 (Jason): "Create payment link" button removed - Email Quote already sends the link. */}
                    <Button color={"primary"} onClick={doEmailQuote}>
                        Email Quote
                    </Button>
                </Box>
                : null}
            {partOrder.status === "QUOTE" ?
                <Box>
                    <Title>Quote calculator</Title>
                    <p className="has-text-grey" style={{marginBottom: "8px"}}>Parts cost plus a margin, plus any labour. Apply sets the customer price on each part - you can still adjust individual lines.</p>
                    <div className="field is-grouped">
                        <div className="control">
                            <label className="label is-small">Margin %</label>
                            <input className="input is-small" type="number" value={qcMargin} onChange={e => setQcMargin(e.target.value)} style={{width: "90px"}} />
                        </div>
                        <div className="control">
                            <label className="label is-small">Labour hrs</label>
                            <input className="input is-small" type="number" value={qcHours} onChange={e => setQcHours(e.target.value)} style={{width: "90px"}} />
                        </div>
                        <div className="control">
                            <label className="label is-small">£ / hr</label>
                            <input className="input is-small" type="number" value={qcRate} onChange={e => setQcRate(e.target.value)} style={{width: "90px"}} />
                        </div>
                    </div>
                    <Button color={"info"} onClick={() => doQuoteCalc(false)}>Calculate</Button>
                    <Button color={"primary"} style={{marginLeft: "8px"}} onClick={() => doQuoteCalc(true)}>Apply to parts</Button>
                    {qcResult ?
                        <Box className={"has-background-info-light"} style={{marginTop: "10px"}}>
                            <p>Parts cost: £{Number(qcResult.parts_cost).toFixed(2)} &rarr; with margin: £{Number(qcResult.parts_customer).toFixed(2)}</p>
                            <p>Labour: {qcResult.labour_hours}h &times; £{Number(qcResult.labour_rate).toFixed(2)} = £{Number(qcResult.labour_total).toFixed(2)}</p>
                            <p><strong>Suggested quote total: £{Number(qcResult.total).toFixed(2)}</strong>{qcResult.applied ? " (applied to " + qcResult.applied + " part(s))" : ""}</p>
                        </Box>
                        : null}
                </Box>
                : null}
            {partOrder.id && partOrder.invoice_number ?
                <Box>
                    <Title>Invoice</Title>
                    <p>Invoice number: <strong>{partOrder.invoice_number}</strong></p>
                    <Button color={"link"} style={{marginTop: "6px"}} onClick={doResendInvoice}>
                        Resend invoice to customer
                    </Button>
                </Box>
                : null}
            {partOrder.id && orderEmail(partOrder) ?
                <Box>
                    <Title>Message customer</Title>
                    {partOrder.awaiting_reply ?
                        <p className="has-text-warning-dark" style={{marginBottom: "8px"}}>Awaiting a reply from the customer.</p> : null}
                    <textarea className="textarea" rows={2}
                        placeholder="Email the customer about this order - replies come back to the order automatically."
                        value={messageText} onChange={e => setMessageText(e.target.value)} disabled={messageBusy}/>
                    <Button color={"info"} style={{marginTop: "8px"}} onClick={doSendMessage} disabled={messageBusy}>
                        {messageBusy ? "Sending..." : "Send Message"}
                    </Button>
                </Box>
                : null}
            {canRefund ?
                <Box>
                    <div style={{cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center"}}
                         onClick={() => setRefundOpen(o => !o)}>
                        <Title style={{marginBottom: 0}}>Refund{partOrder.refunded ? " (already refunded)" : ""}</Title>
                        <span className="has-text-grey">{refundOpen ? "▾ hide" : "▸ show"}</span>
                    </div>
                    {refundOpen ?
                        <div style={{marginTop: "12px"}}>
                            {refundSuggestion ?
                                <p className="has-text-grey" style={{marginBottom: "8px"}}>
                                    Suggested: <strong>£{Number(refundSuggestion.suggested).toFixed(2)}</strong> — {refundSuggestion.basis}
                                </p> : null}
                            <Columns isMultiline>
                                <Column>
                                    <label className="label">Refund by</label>
                                    <div className="select">
                                        <select value={refundMode} onChange={e => setRefundMode(e.target.value)} disabled={refundBusy || partOrder.refunded}>
                                            <option value="rule">Suggested (rule-based)</option>
                                            <option value="percent">% of part price</option>
                                            <option value="amount">£ amount (manual)</option>
                                        </select>
                                    </div>
                                </Column>
                                {refundMode !== "rule" ?
                                    <Column>
                                        <label className="label">{refundMode === "percent" ? "Percentage (%)" : "Amount (£)"}</label>
                                        <Input
                                            type={"number"}
                                            min={0}
                                            step={refundMode === "percent" ? 1 : 0.01}
                                            value={refundValue}
                                            onChange={e => setRefundValue(e.target.value)}
                                            disabled={refundBusy || partOrder.refunded}
                                        />
                                    </Column> : null}
                                <Column>
                                    <label className="label white">.</label>
                                    <Button color={"danger"} onClick={doRefund} disabled={refundBusy || partOrder.refunded}>
                                        {partOrder.refunded ? "Already Refunded" : (refundBusy ? "Refunding..." : "Refund")}
                                    </Button>
                                </Column>
                            </Columns>
                            {refundResult && refundResult.customer_refund ?
                                <Columns isMultiline>
                                    <Column>
                                        <Box className={"has-background-warning-light"}>
                                            <p>
                                                <strong>Customer refund: £{refundResult.customer_refund}</strong>
                                                {refundResult.demo ? " (demo — no real money moved)" : (refundResult.refunded ? " — sent to Worldpay" : " — NOT completed")}
                                            </p>
                                            <p>Van Parts Direct credited £{refundResult.vanparts_credit}; Rapid Fit retains £{refundResult.rapid_retained} (10% handling, inter-company).</p>
                                            <p className={"has-text-grey"}>Internal credit note #{refundResult.credit_note_id} ({refundResult.credit_note_status}) — for the accountancy step to approve.</p>
                                        </Box>
                                    </Column>
                                </Columns>
                                : null}
                        </div>
                        : null}
                </Box>
                : null}
            <Box>
                <Title>Audit trail</Title>
                {events && events.length ?
                    <table className={"table is-fullwidth is-striped is-narrow"}>
                        <thead>
                            <tr><th>When</th><th>User</th><th>Action</th><th>Detail</th></tr>
                        </thead>
                        <tbody>
                            {events.map((e, i) =>
                                <tr key={i}>
                                    <td>{e.at ? new Date(e.at).toLocaleString() : ""}</td>
                                    <td>{e.user}</td>
                                    <td>{e.action === "fedex" ? "FedEx" : e.action}</td>
                                    <td>{e.full_text ?
                                        <a href="#" title="Show the full message" onClick={ev => { ev.preventDefault(); setAuditPopup({title: (e.user || "") + " - " + (e.at ? new Date(e.at).toLocaleString() : ""), text: e.full_text}); }}>{e.detail}</a>
                                        : linkTracking(e.detail)}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    : <p className={"has-text-grey"}>No actions recorded yet.</p>}
                {auditPopup ?
                    <div className="modal is-active">
                        <div className="modal-background" onClick={() => setAuditPopup(null)}></div>
                        <div className="modal-card">
                            <header className="modal-card-head">
                                <p className="modal-card-title" style={{fontSize: "1.1rem"}}>Message - {auditPopup.title}</p>
                                <button className="delete" aria-label="close" onClick={() => setAuditPopup(null)}></button>
                            </header>
                            <section className="modal-card-body"><div style={{whiteSpace: "pre-wrap"}}>{auditPopup.text}</div></section>
                            <footer className="modal-card-foot"><button className="button" onClick={() => setAuditPopup(null)}>Close</button></footer>
                        </div>
                    </div> : null}
            </Box>
            {overCredit ?
                <div className="notification is-danger">
                    <strong>OVER CREDIT LIMIT.</strong> This order (£{_orderIncVat.toFixed(2)} inc VAT) takes the customer past their Van Parts credit limit of £{_vpLimit.toFixed(2)}
                    {creditInfo && creditInfo.xero_connected ? <span> (credit left £{Number(creditInfo.credit_left).toFixed(2)})</span> : null}.
                    {" "}The order can still go ahead — please flag it with accounts.
                </div> : null}
            <Box>
                <Title>Parts</Title>
                <Columns isMultiline>
                    <Column>
                        <label className="label">Total Cost</label>
                        <Input
                            disabled={true}
                            value={"£"+partOrder.total_part_cost}
                        />
                    </Column>
                    <Column>
                        <label className="label">Total Customer Charge</label>
                        <Input
                            disabled={true}
                            value={"£"+partOrder.total_part_customer_charge}
                        />
                    </Column>
                    <Column>
                        <label className="label">Total Customer Charge (Inc. VAT)</label>
                        <Input
                            disabled={true}
                            value={"£"+partOrder.total_part_customer_charge_inc_vat}
                        />
                    </Column>
                    <Column>
                        <label className="label">Customer Amount Paid</label>
                        <Input
                            type={"number"}
                            value={partOrder.customer_paid_amount}
                            onChange={onChange}
                            name={"customer_paid_amount"}
                            disabled={getDisabled()}
                        />
                    </Column>
                    <Column>
                        <label className="label">Customer Payment Method</label>
                        <Select
                          name={"customer_payment_method"}
                          value={partOrder.customer_payment_method}
                          onChange={onChange}
                        >
                          {customerPaymentOptions.map(method => (
                              <option value={method.value}>{method.name}</option>
                          ))}
                        </Select>
                    </Column>
                </Columns>
                <Columns isMultiline>
                    <Column>
                        <TrackingOptionsPicker
                            label={"Bulk Part Expense Tracking Option"}
                            options={trackingOptions}
                            name={"supplier_tracking_option"}
                            editable={true}
                            inlineEdit={false}
                            blank={true}
                            disabled={getDisabled()}
                            onChange={onBulkPartTrackingOptionChange}
                            className={" "}
                        />
                    </Column>
                    <Column>
                        <TrackingOptionsPicker
                            label={"Bulk Workshop Invoicing Tracking Option"}
                            options={trackingOptions}
                            name={"customer_tracking_option"}
                            editable={true}
                            inlineEdit={false}
                            blank={true}
                            disabled={getDisabled()}
                            onChange={onBulkPartTrackingOptionChange}
                            className={" "}
                        />
                    </Column>
                    <Column></Column>
                    <Column></Column>
                    <Column></Column>
                    <Column></Column>
                    <Column></Column>
                </Columns>
                <Columns isMultiline>
                    <Column>
                        <Table isFullwidth>
                            <thead>
                              <tr>
                                <td>Name</td>
                                <td>Supplier</td>
                                <td>Part Expense Tracking Option</td>
                                <td>Workshop Invoicing Tracking Option</td>
                                <td>Date Ordered</td>
                                <td>Part Number</td>
                                <td>Invoice No</td>
                                <td>Tracking #</td>
                                <td>Carrier</td>
                                <td>Arrived</td>
                                <td>Actual Cost</td>
                                <td>Actual Charge</td>
                                <td>VAT Rate</td>
                                <td>Comments</td>
                                <td>Override Cost</td>
                                <td>Postage</td>
                                <td>Stock Accrual</td>
                                <td>Backorder Date</td>
                                <td>To Be Ordered</td>
                                <td>Non-returnable</td>
                                <td>Remove</td>
                              </tr>
                            </thead>
                            <tbody data-testid="parts">
                              {partOrder.parts ? partOrder.parts.map((part, index) => (
                                    <PartRow
                                        part={part}
                                        onPartChange={onPartChange}
                                        onPartDelete={onPartDelete}
                                        index={index}
                                        appDefaults={appDefaults}
                                        partOrder={partOrder}
                                        disabled={getDisabled()}
                                        trackingOptions={trackingOptions}
                                        trackingOptionObjects={trackingOptionObjects}
                                    />
                              )) : <tr></tr>}
                            </tbody>
                        </Table>
                        {!getDisabled() && (partOrder.status !== "ENQUIRY") && (
                            <div>
                                <MaxusPartSearch onSelect={onMaxusPartSelect} disabled={getDisabled()} />
                                <button type="button" className="button is-small"
                                    style={{ marginBottom: "0.75rem" }}
                                    onClick={() => setCatalogueOpen(true)}>
                                    Browse Maxus catalogue (diagrams)
                                </button>
                            </div>
                        )}
                        <MaxusCatalogueOverlay
                            open={catalogueOpen}
                            vin={partOrder.customer_vehicle_vin}
                            onClose={() => setCatalogueOpen(false)}
                            onSelect={(p) => { onMaxusPartSelect(p); setCatalogueOpen(false); }}
                        />
                        {postageOpen ?
                            <div className="box" style={{maxWidth: "620px", marginBottom: "0.75rem"}}>
                                <label className="label">Calculate postage (FedEx Priority, as the website)</label>
                                <div className="field has-addons">
                                    <div className="control">
                                        <input className="input" placeholder="Delivery postcode" value={postagePc}
                                               onChange={e => setPostagePc(e.target.value.toUpperCase())}
                                               onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); calcPostage(); } }}/>
                                    </div>
                                    <div className="control">
                                        <button type="button" className={"button is-info" + (postageBusy ? " is-loading" : "")}
                                                onClick={calcPostage} disabled={postageBusy}>Calculate</button>
                                    </div>
                                    <div className="control">
                                        <button type="button" className="button" onClick={() => setPostageOpen(false)}>Close</button>
                                    </div>
                                </div>
                                {postageRes && postageRes.detail ?
                                    <p className="has-text-danger">{postageRes.detail}</p> : null}
                                {postageRes && !postageRes.detail ?
                                    <div>
                                        <p className="is-size-7 has-text-grey">
                                            {postageRes.items.map(it => (it.part_number || it.name) + (it.qty > 1 ? " x" + it.qty : "") + " " + it.weight + "kg" + (it.box ? " (" + it.box + ")" : "") + (it.freight ? " [large]" : "") + (it.found ? "" : " (not on website - 0.5kg assumed)")).join(" · ")}
                                            {" "}= {postageRes.total_weight}kg in {postageRes.packages} box{postageRes.packages > 1 ? "es" : ""}{postageRes.small_in_box ? " (small parts packed in the bumper box)" : ""} to {postageRes.postcode}
                                        </p>
                                        {postageRes.quote_required ?
                                            <div className="notification is-warning is-light" style={{marginTop: "0.5rem"}}>
                                                <strong>{postageRes.collect_only ? "Collect or own courier only" : "Delivery quote required"}</strong>
                                                <ul>{postageRes.reasons.map((r, k) => <li key={k}>{r}</li>)}</ul>
                                                {postageRes.collect_only ? "We don't send glass - the customer collects it or arranges their own courier."
                                                    : "Add the carriage line by hand once the delivery has been quoted."}
                                            </div>
                                        : postageRes.large && postageRes.options && postageRes.options.length ?
                                            // 14 Oct: large items - FedEx (parcel or freight, +leeway) vs the DVR van per mile; cheapest first
                                            <div style={{marginTop: "0.5rem"}}>
                                                <p className="is-size-7">Large item delivery - FedEx price + {postageRes.leeway_pct}% leeway + £{Number(postageRes.handling).toFixed(2)} handling, or the DVR van at £2/mile{postageRes.miles ? " (" + postageRes.miles + " miles" + (postageRes.miles_how !== "road" ? ", estimated" : "") + ")" : ""}. Prices + VAT.</p>
                                                {[...postageRes.options].sort((a, b) => a.charge - b.charge).map((o, k) =>
                                                    <div key={k} style={{marginTop: "0.3rem"}}>
                                                        <button type="button" className={"button is-small " + (o.best ? "is-success" : "is-light")}
                                                                onClick={() => applyPostage(o.charge, o)}>
                                                            {(partOrder.parts || []).some(pt => pt.is_postage) ? "Update" : "Add"} carriage £{o.charge.toFixed(2)} - {o.label}{o.best ? " (cheapest)" : ""}
                                                        </button>
                                                        {o.cost ? <span className="is-size-7 has-text-grey"> FedEx cost £{o.cost.toFixed(2)}</span> : null}
                                                    </div>)}
                                                {postageRes.fedex_error ? <p className="is-size-7 has-text-danger">{postageRes.fedex_error}</p> : null}
                                            </div>
                                        : postageRes.error ?
                                            <div style={{marginTop: "0.5rem"}}>
                                                <p className="has-text-danger">{postageRes.error}</p>
                                                <button type="button" className="button is-small" style={{marginTop: "0.4rem"}}
                                                        onClick={() => applyPostage(postageRes.fallback)}>
                                                    Use standard charge £{Number(postageRes.fallback).toFixed(2)}
                                                </button>
                                            </div>
                                        :
                                            <div style={{marginTop: "0.5rem"}}>
                                                <p>FedEx £{postageRes.fedex_amount.toFixed(2)} + £{Number(postageRes.handling).toFixed(2)} handling{postageRes.floored ? " - under the £" + Number(postageRes.minimum).toFixed(2) + " minimum, so" : ""} = <strong>£{postageRes.charge.toFixed(2)}</strong> + VAT{postageRes.transit ? " (" + postageRes.transit.toLowerCase().replace(/_/g, " ") + ")" : ""}</p>
                                                <button type="button" className="button is-success is-small" style={{marginTop: "0.4rem"}}
                                                        onClick={() => applyPostage(postageRes.charge)}>
                                                    {(partOrder.parts || []).some(pt => pt.is_postage) ? "Update" : "Add"} carriage £{postageRes.charge.toFixed(2)}
                                                </button>
                                            </div>}
                                    </div> : null}
                            </div> : null}
                        <Button
                            color={"info"}
                            onClick={addPart}
                            disabled={getDisabled() || (partOrder.status === "ENQUIRY")}
                        >
                            Add Part +
                        </Button>
                        {!getDisabled() && partOrder.status !== "ENQUIRY" && !partOrder.fedex_tracking ?
                            <Button color={"info"} outlined onClick={openPostage} style={{marginLeft: "0.5rem"}}>
                                Calculate postage
                            </Button> : null}
                        {partOrder.id && ["ORDER", "COMPLETE"].includes(partOrder.status) && !partOrder.fedex_tracking
                            && !["to_quote", "requested", "paid"].includes(partOrder.postage_status || "") ?
                            <Button color={"link"} outlined onClick={() => { setPostageReqOpen(true); window.scrollTo(0, 0); }} style={{marginLeft: "0.5rem"}}>
                                Postage request
                            </Button> : null}
                        <Button
                            color={"info"}
                            onClick={onSearchPartClick}
                            disabled={getDisabled() || (partOrder.status === "ENQUIRY")}
                        >
                            Search Parts
                        </Button>
                        <Button
                            color={"info"}
                            onClick={onSearchWorkClick}
                            disabled={getDisabled() || (partOrder.status === "ENQUIRY")}
                        >
                            Search Work
                        </Button>
                    </Column>
                </Columns>
            </Box>
            <Box>
                <Columns isMultiline>
                    <Column>
                        <Button
                            color={"success"}
                            onClick={handleSave}
                            disabled={getDisabled()}
                            isFullWidth
                        >
                            Save
                        </Button>
                    </Column>
                </Columns>
                {!(partOrder.department && partOrder.department.id === 9) ?
                <Columns isMultiline>
                    <Column>
                        <Button
                            color={"success"}
                            disabled={getDisabled() || partOrder.status !== "ORDER"}
                            onClick={onCompleteClick}
                            isFullWidth
                        >
                            Complete
                        </Button>
                    </Column>
                </Columns>
                : null}
                <Columns isMultiline>
                    <Column>
                        <Button
                            color="warning"
                            disabled={(partOrder.status !== "ORDER" && partOrder.status !== "COMPLETE")}
                            onClick={handlePrint}
                            isFullWidth
                        >
                          Print Part Order
                        </Button>
                    </Column>
                </Columns>
                <Columns isMultiline>
                    <Column>
                        <Button
                            color="warning"
                            disabled={(partOrder.status !== "ORDER" && partOrder.status !== "COMPLETE" && partOrder.status !== "QUOTE")}
                            onClick={handleQuotePrint}
                            isFullWidth
                        >
                          Print Part Order Quote
                        </Button>
                    </Column>
                </Columns>
                <Columns isMultiline>
                    <Column>
                        <Button
                            color={"danger"}
                            onClick={onDelete}
                            disabled={getDisabled()}
                            isFullWidth
                        >
                            Delete
                        </Button>
                    </Column>
                </Columns>
            </Box>
            <LabelPrinterModal open={printerOpen} setOpen={setPrinterOpen}/>
            <FedexLabelModal
                open={fedexOpen}
                setOpen={setFedexOpen}
                partOrder={partOrder}
                replace={fedexReplace}
                onBooked={onFedexBooked}
            />
            <PartSearchModal
                {...partsModalState}
                setModalState={setPartsModalState}
                selectPart={searchSelectPart}
            />
            <WorkSearchModal
              {...workModalState}
              setModalState={setWorkModalState}
              selectWork={searchSelectWork}
              supplier={{}}
            />
            <TrackingOptionModal
                partOrder={partOrder}
                open={completeModalOpen}
                setOpen={setCompleteModalOpen}
                trackingOptions={trackingOptions}
                onTrackingOptionChange={onTrackingOptionChange}
                onComplete={onComplete}
            />
            <div style={{display: "none"}}>
                <div ref={printPartRef}>
                    <PrintPartOrder
                        data={partOrder}
                    />
                </div>
                <div ref={printOrderQuoteRef}>
                    <PrintQuote
                        data={partOrder}
                    />
                </div>
            </div>
        </div>
    );


}